import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import StatusPill from "@/components/StatusPill";
import { fmtMoney, fmtDate, titleCase } from "@/lib/format";
import { Truck as TruckIcon, Wrench, AlertTriangle, Gauge, Plus, Trash2 } from "lucide-react";
import KPICard from "@/components/KPICard";
import { toast } from "sonner";
import { FleetOps } from "@/components/FleetOps";

const TRUCK_TYPES = ["sideload", "rearload", "frontload", "rolloff"];

export default function Fleet() {
    const [trucks, setTrucks] = useState([]);
    const [maint, setMaint] = useState([]);
    const [openAdd, setOpenAdd] = useState(false);

    const load = () =>
        Promise.all([api.get("/fleet/trucks"), api.get("/fleet/maintenance")])
               .then(([t, m]) => { setTrucks(t.data); setMaint(m.data); });

    useEffect(() => { load(); }, []);

    const active = trucks.filter((t) => t.status === "active").length;
    const inMaint = trucks.filter((t) => t.status === "maintenance").length;
    const totalFuel = trucks.reduce((s, t) => s + (t.fuel_cost_mtd || 0), 0);

    const deleteTruck = async (t) => {
        if (!window.confirm(`Remove truck "${t.name}"?`)) return;
        try {
            await api.delete(`/fleet/trucks/${t.id}`);
            toast.success(`Removed ${t.name}`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Delete failed");
        }
    };

    return (
        <div data-testid="fleet-page">
            <PageHeader
                title="Fleet Management"
                subtitle="Trucks, maintenance, fuel, registration — your equipment health in one place."
                actions={
                    <Button onClick={() => setOpenAdd(true)} data-testid="add-truck-btn">
                        <Plus className="w-4 h-4 mr-1" />Add truck
                    </Button>
                }
            />
            <div className="px-8 py-6 space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <KPICard testid="fleet-kpi-total" label="Total trucks" value={trucks.length} icon={TruckIcon} />
                    <KPICard testid="fleet-kpi-active" label="Active" value={active} icon={Gauge}
                             delta={`${trucks.length ? Math.round(active/trucks.length*100) : 0}% available`} deltaTone="success" />
                    <KPICard testid="fleet-kpi-maint" label="In maintenance" value={inMaint} icon={Wrench}
                             deltaTone={inMaint ? "destructive" : "muted"}
                             delta={inMaint ? "Action needed" : "All clear"} />
                    <KPICard testid="fleet-kpi-fuel" label="Fuel MTD" value={fmtMoney(totalFuel)} icon={AlertTriangle}
                             delta="Across all trucks" />
                </div>

                <Card>
                    <div className="overflow-x-auto" data-testid="trucks-table">
                        <table className="w-full text-sm">
                            <thead>
                            <tr className="border-b border-border surface">
                                <th className="text-left px-4 py-3 overline">Unit</th>
                                <th className="text-left px-4 py-3 overline">Type</th>
                                <th className="text-left px-4 py-3 overline">VIN / Plate</th>
                                <th className="text-right px-4 py-3 overline">Mileage</th>
                                <th className="text-left px-4 py-3 overline">Next service</th>
                                <th className="text-right px-4 py-3 overline">Fuel MTD</th>
                                <th className="text-left px-4 py-3 overline">Status</th>
                                <th className="px-4 py-3"></th>
                            </tr>
                            </thead>
                            <tbody>
                            {trucks.length === 0 && (
                                <tr>
                                    <td colSpan="8" className="px-4 py-10 text-center text-muted-foreground text-sm">
                                        No trucks yet. Click <span className="font-medium">Add truck</span> to register your first one.
                                    </td>
                                </tr>
                            )}
                            {trucks.map((t) => (
                                <tr key={t.id} className="border-b border-border hover:bg-secondary/40" data-testid={`truck-row-${t.id}`}>
                                    <td className="px-4 py-3 font-medium">{t.name}</td>
                                    <td className="px-4 py-3 text-muted-foreground">{titleCase(t.type)}</td>
                                    <td className="px-4 py-3 text-xs font-mono text-muted-foreground">{t.vin?.slice(-8)} · {t.plate}</td>
                                    <td className="px-4 py-3 text-right tabular">{t.mileage?.toLocaleString()}</td>
                                    <td className="px-4 py-3 text-muted-foreground">{fmtDate(t.next_service_due)}</td>
                                    <td className="px-4 py-3 text-right tabular">{fmtMoney(t.fuel_cost_mtd)}</td>
                                    <td className="px-4 py-3"><StatusPill status={t.status} /></td>
                                    <td className="px-4 py-3 text-right">
                                        <Button size="sm" variant="ghost"
                                                onClick={() => deleteTruck(t)}
                                                data-testid={`delete-truck-${t.id}`}
                                                className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30">
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </Card>

                <Card>
                    <CardContent className="p-6">
                        <div className="overline mb-1">Recent</div>
                        <h3 className="font-heading text-lg font-semibold mb-4">Maintenance log</h3>
                        <div className="text-sm text-muted-foreground">
                            {maint.length === 0 ? "No maintenance records logged yet." : `${maint.length} ${maint.length === 1 ? "entry" : "entries"}`}
                        </div>
                    </CardContent>
                </Card>
                {trucks.length > 0 && <FleetOps trucks={trucks} onChanged={load} />}
            </div>

            <AddTruckDialog open={openAdd} setOpen={setOpenAdd} onSaved={load} />
        </div>
    );
}


function AddTruckDialog({ open, setOpen, onSaved }) {
    const emptyForm = { name: "", type: "sideload", vin: "", plate: "", year: new Date().getFullYear() };
    const [form, setForm] = useState(emptyForm);
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) {
            toast.error("Truck name/number is required");
            return;
        }
        setBusy(true);
        try {
            await api.post("/fleet/trucks", {
                name: form.name.trim(),
                type: form.type,
                vin: form.vin.trim(),
                plate: form.plate.trim(),
                year: parseInt(form.year, 10) || new Date().getFullYear(),
            });
            toast.success(`${form.name} added to fleet`);
            setForm(emptyForm);
            setOpen(false);
            await onSaved();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not add truck");
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent className="max-w-md" data-testid="add-truck-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading">Add a truck</DialogTitle>
                    <DialogDescription>
                        Register a new vehicle in your fleet. Mileage, fuel, and next-service dates
                        can be filled in later from the truck's row.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                    <div>
                        <Label className="text-xs mb-1 block">Truck number / name *</Label>
                        <Input value={form.name} placeholder="Truck 1"
                               data-testid="truck-name-input"
                               onChange={(e) => setForm({ ...form, name: e.target.value })} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs mb-1 block">Type</Label>
                            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                                <SelectTrigger data-testid="truck-type-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {TRUCK_TYPES.map((t) => (
                                        <SelectItem key={t} value={t}>{titleCase(t)}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs mb-1 block">Year</Label>
                            <Input type="number" value={form.year}
                                   data-testid="truck-year-input"
                                   onChange={(e) => setForm({ ...form, year: e.target.value })} />
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs mb-1 block">Plate</Label>
                        <Input value={form.plate} placeholder="ABC-1234"
                               data-testid="truck-plate-input"
                               onChange={(e) => setForm({ ...form, plate: e.target.value })} />
                    </div>
                    <div>
                        <Label className="text-xs mb-1 block">VIN (optional)</Label>
                        <Input value={form.vin} placeholder="17-digit VIN"
                               data-testid="truck-vin-input"
                               onChange={(e) => setForm({ ...form, vin: e.target.value })} />
                    </div>
                    <DialogFooter className="pt-2">
                        <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button type="submit" disabled={busy} data-testid="save-truck-btn">
                            {busy ? "Saving…" : "Add truck"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
